import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from '../../firebase';
import { collection, getDocs, query, doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useReactToPrint } from 'react-to-print';

// MUI Components
import {
    Paper, Typography, TextField, Button, Box,
    Divider, Grid, MenuItem, Autocomplete, CircularProgress, Card, CardContent
} from '@mui/material';
import {
    Print as PrintIcon, Search as SearchIcon
} from '@mui/icons-material';

const Payslip = () => {
    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filter States
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [payslipData, setPayslipData] = useState(null);

    const printRef = useRef();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            await fetchStaffWithAllocations();
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const fetchStaffWithAllocations = async () => {
        try {
            // Pulls baseline structures assigned globally
            const q = query(collection(db, "salaryAllocations"));
            const snap = await getDocs(q);
            const list = snap.docs.map(doc => ({
                id: doc.id,
                staffId: doc.data().staffId || doc.id,
                label: (doc.data().staffName || "Unknown Staff").toUpperCase(),
                ...doc.data()
            }));
            setStaffList(list);
        } catch (error) {
            console.error("Error fetching allocations for payslips:", error);
        }
    };

    const handleGenerate = () => {
        if (!selectedStaff) return alert("Please select a staff member");
        setPayslipData(selectedStaff);
    };

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: `Payslip_${payslipData?.staffName || 'Staff'}_${month}_${year}`,
    });

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                <CircularProgress size={35} />
            </Box>
        );
    }

    return (
        <Box p={1} bgcolor="#f0f2f5" minHeight="100vh">
            <Grid className="row" sx={{ justifyContent: 'center' }}>
                <Grid className="col-sm-12" item xs={12} md={9}>
                    {/* Filter Inputs Area */}
                    <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }} color="textPrimary">
                            Generate Staff Payslip
                        </Typography>
                        <Grid className="row" spacing={2} alignItems="center">
                            <Grid className="col-sm-3 mb-2" item xs={12} sm={5}>
                                <Autocomplete
                                    options={staffList}
                                    getOptionLabel={(option) => option.label || ""}
                                    value={selectedStaff}
                                    onChange={(e, val) => setSelectedStaff(val)}
                                    renderInput={(params) => <TextField {...params} label="Select Target Employee" size="small" />}
                                />
                            </Grid>
                            <Grid className="col-sm-3 mb-2" item xs={6} sm={2.5}>
                                <TextField select fullWidth label="Month" size="small" value={month} onChange={(e) => setMonth(e.target.value)}>
                                    {months.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid className="col-sm-3 mb-2" item xs={6} sm={2}>
                                <TextField fullWidth label="Year" type="number" size="small" value={year} onChange={(e) => setYear(e.target.value)} />
                            </Grid>
                            <Grid className="col-sm-3 mb-2" item xs={12} sm={2.5}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={<SearchIcon />}
                                    onClick={handleGenerate}
                                    sx={{ textTransform: 'none', height: '40px', borderRadius: 2 }}
                                >
                                    View Slip
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Interactive Preview Container */}
                    {payslipData && (
                        <Box sx={{ mb: 5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<PrintIcon />}
                                    onClick={handlePrint}
                                    sx={{ textTransform: 'none', borderRadius: 1.5 }}
                                >
                                    Print Payslip
                                </Button>
                            </Box>

                            {/* Printable Render Matrix Template */}
                            <Card sx={{ border: '1px solid #e0e0e0', boxShadow: 'none', borderRadius: 3 }}>
                                <CardContent ref={printRef} sx={{ p: '40px !important' }}>
                                    {/* Corporate Style Heading */}
                                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                                        <Typography variant="h5" fontWeight={800} letterSpacing={0.5} color="primary" gutterBottom>
                                            GIS World Hospitality Services Pvt. Ltd.
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Office No A-16, Shrinath Plaza, Dnyaneshwar Paduka Chowk, F.C. Road, Shivajinagar, Pune – 411005
                                        </Typography>
                                        <Typography variant="subtitle2" sx={{ mt: 3, fontWeight: 700, color: '#333', letterSpacing: 1 }}>
                                            SALARY DISBURSEMENT STATEMENT FOR {months[month - 1].toUpperCase()} {year}
                                        </Typography>
                                    </Box>

                                    <Divider sx={{ my: 2.5, borderBottomWidth: 1.5 }} />

                                    {/* Meta Information Key Values */}
                                    <Grid container spacing={3} sx={{ mb: 4 }}>
                                        <Grid item xs={6}>
                                            <Box display="flex" flexDirection="column" gap={0.5}>
                                                <Typography variant="body2"><b>Staff Member:</b> {payslipData.staffName}</Typography>
                                                <Typography variant="body2"><b>Identifier Ref:</b> {payslipData.staffId ? payslipData.staffId.slice(0, 10).toUpperCase() : 'N/A'}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} sx={{ textAlign: 'right' }}>
                                            <Box display="flex" flexDirection="column" gap={0.5}>
                                                <Typography variant="body2"><b>Distribution Route:</b> {payslipData.paymentMode}</Typography>
                                                <Typography variant="body2"><b>Class Designation:</b> General Staff</Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {/* High Contrast Ledger Grid Table */}
                                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0' }}>
                                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#555', fontSize: '0.85rem' }}>Compensation Line Items</th>
                                                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: '#555', fontSize: '0.85rem' }}>Amount (₹)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr style={{ borderBottom: '1px solid #f1f1f1' }}>
                                                <td style={{ padding: '12px 16px', color: '#333', fontSize: '0.9rem' }}>Base Compensation Package</td>
                                                <td style={{ padding: '12px 16px', textAlign: 'right', color: '#333', fontSize: '0.9rem' }}>₹{(payslipData.basic || 0).toLocaleString('en-IN')}</td>
                                            </tr>
                                            <tr style={{ borderBottom: '1px solid #f1f1f1' }}>
                                                <td style={{ padding: '12px 16px', color: '#333', fontSize: '0.9rem' }}>House Rent Allowance (HRA)</td>
                                                <td style={{ padding: '12px 16px', textAlign: 'right', color: '#333', fontSize: '0.9rem' }}>₹{(payslipData.hra || 0).toLocaleString('en-IN')}</td>
                                            </tr>
                                            <tr style={{ borderBottom: '1px solid #f1f1f1' }}>
                                                <td style={{ padding: '12px 16px', color: '#333', fontSize: '0.9rem' }}>Dearness Allowance (DA)</td>
                                                <td style={{ padding: '12px 16px', textAlign: 'right', color: '#333', fontSize: '0.9rem' }}>₹{(payslipData.da || 0).toLocaleString('en-IN')}</td>
                                            </tr>
                                            <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                                                <td style={{ padding: '12px 16px', color: '#333', fontSize: '0.9rem' }}>Travel Allowance (TA)</td>
                                                <td style={{ padding: '12px 16px', textAlign: 'right', color: '#333', fontSize: '0.9rem' }}>₹{(payslipData.ta || 0).toLocaleString('en-IN')}</td>
                                            </tr>
                                            <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 700 }}>
                                                <td style={{ padding: '14px 16px', color: '#0d47a1', fontSize: '0.95rem' }}>GROSS DISBURSEMENT TOTAL</td>
                                                <td style={{ padding: '14px 16px', textAlign: 'right', color: '#0d47a1', fontSize: '0.95rem' }}>₹{(payslipData.totalSalary || 0).toLocaleString('en-IN')}</td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    {/* Electronic Fund Transfer Info Wrapper */}
                                    {payslipData.paymentMode === 'Bank Transfer' && payslipData.accountNo && (
                                        <Box sx={{ mt: 4, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                                            <Typography variant="caption" fontWeight={800} color="textSecondary" display="block" sx={{ mb: 1, letterSpacing: 0.5 }}>
                                                DESTINATION ROUTING PARAMS
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={4}><Typography variant="caption" color="textSecondary"><b>Bank:</b> {payslipData.bankName || 'N/A'}</Typography></Grid>
                                                <Grid item xs={4}><Typography variant="caption" color="textSecondary"><b>Account No:</b> {payslipData.accountNo || 'N/A'}</Typography></Grid>
                                                <Grid item xs={4}><Typography variant="caption" color="textSecondary"><b>IFSC Code:</b> {payslipData.ifsc || 'N/A'}</Typography></Grid>
                                            </Grid>
                                        </Box>
                                    )}

                                    {/* Signature Endorsement Block */}
                                    <Grid className="row" sx={{ mt: 12 }}>
                                        <Grid className="col-sm-6" item xs={6}>
                                            <Divider sx={{ width: '160px', mb: 1, borderColor: '#aaa' }} />
                                            <Typography variant="caption" color="textSecondary" fontWeight={600}>Recipient Endorsement</Typography>
                                        </Grid>
                                        <Grid className="col-sm-6" item xs={6} sx={{ textAlign: 'right' }}>
                                            <Divider sx={{ width: '160px', ml: 'auto', mb: 1, borderColor: '#aaa' }} />
                                            <Typography variant="caption" color="textSecondary" fontWeight={600}>Authorized Clearance Signatory</Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Box>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

export default Payslip;